import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import TermsModal from '../components/TermsModal';
import DashboardHeader from '../components/DashboardHeader';
import './AuthForm.css';

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showGreenBar, setShowGreenBar] = useState(true);

  // Hide green bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY === 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error for this field when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
      newErrors.firstName = 'First name can only contain letters';
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName)) {
      newErrors.lastName = 'Last name can only contain letters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    }

    // Confirm Password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms and Conditions validation
    if (!agreedToTerms) {
      newErrors.terms = 'You must agree to the Terms and Conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate form before submitting
    if (!validateForm()) {
      return;
    }

    try {
      // Remove confirmPassword before sending to backend
      const { confirmPassword, ...registerData } = formData;

      const res = await fetch(`${process.env.REACT_APP_API}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerData),
      });

      const data = await res.json();

      if (res.ok) {
        if (data.requiresVerification) {
          setShowSuccessModal(true);
        } else {
          setShowSuccessModal(true);
        }
      } else {
        // Handle error responses
        if (res.status === 400 && data.message && data.message.includes('already exists')) {
          // User already exists
          setErrors({ email: 'An account with this email already exists' });
        } else if (res.status === 429) {
          // Rate limit error
          setErrors({ email: data.message || 'Too many registration attempts. Please wait 5 minutes before trying again.' });
        } else {
          setErrors({ email: data.message || 'Registration failed. Please try again.' });
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
      alert('Server error. Please try again later.');
    }
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar - only visible at top */}
      {showGreenBar && (
        <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
      )}
      {/* Navbar always sticky at top */}
      <div style={{ 
        width: '100%', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
        position: 'sticky',
        top: 0, 
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>

      <motion.div 
        className="auth-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        style={{ paddingTop: '40px', paddingBottom: '40px' }}
      >
        <motion.form 
          className="auth-form" 
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
        >
        <h2>Sign Up</h2>
        <div className="form-group">
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            className={errors.firstName ? 'error' : ''}
          />
          {errors.firstName && <span className="error-message">{errors.firstName}</span>}
        </div>
        <div className="form-group">
          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            className={errors.lastName ? 'error' : ''}
          />
          {errors.lastName && <span className="error-message">{errors.lastName}</span>}
        </div>
        <div className="form-group">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>
        <div className="form-group">
          <input
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            className={errors.password ? 'error' : ''}
          />
          {errors.password && <span className="error-message">{errors.password}</span>}
          
          {/* Password Requirements - Only show when typing */}
          {formData.password && (
            <div style={{ 
              marginTop: '8px', 
              padding: '12px', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              fontSize: '0.85rem',
              color: '#495057'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '6px', color: '#2c3e50' }}>Password must contain:</div>
              <ul style={{ 
                margin: 0, 
                paddingLeft: '20px',
                listStyle: 'none'
              }}>
                <li style={{ 
                  marginBottom: '4px',
                  color: formData.password.length >= 6 ? '#28a745' : '#6c757d'
                }}>
                  {formData.password.length >= 6 ? '✓' : '○'} At least 6 characters
                </li>
                <li style={{ 
                  marginBottom: '4px',
                  color: /(?=.*[a-z])/.test(formData.password) ? '#28a745' : '#6c757d'
                }}>
                  {/(?=.*[a-z])/.test(formData.password) ? '✓' : '○'} One lowercase letter
                </li>
                <li style={{ 
                  marginBottom: '4px',
                  color: /(?=.*[A-Z])/.test(formData.password) ? '#28a745' : '#6c757d'
                }}>
                  {/(?=.*[A-Z])/.test(formData.password) ? '✓' : '○'} One uppercase letter
                </li>
                <li style={{ 
                  color: /(?=.*\d)/.test(formData.password) ? '#28a745' : '#6c757d'
                }}>
                  {/(?=.*\d)/.test(formData.password) ? '✓' : '○'} One number
                </li>
              </ul>
            </div>
          )}
        </div>
        <div className="form-group">
          <input
            type="password"
            name="confirmPassword"
            placeholder="Confirm Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={errors.confirmPassword ? 'error' : ''}
          />
          {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
        </div>
        
        <div className="form-group terms-section">
          <div className="terms-info-box">
            By checking the box, you are agreeing to our{' '}
            <span
              onClick={() => setShowTermsModal(true)}
              style={{
                color: '#2563eb',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                cursor: 'pointer',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.color = '#1d4ed8'}
              onMouseLeave={(e) => e.target.style.color = '#2563eb'}
            >
              Terms of Use
            </span>
            {' '}and our{' '}
            <span
              onClick={() => setShowPrivacyModal(true)}
              style={{
                color: '#2563eb',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                cursor: 'pointer',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.color = '#1d4ed8'}
              onMouseLeave={(e) => e.target.style.color = '#2563eb'}
            >
              Privacy Policy
            </span>
          </div>
          
          <div 
            className={`terms-checkbox-wrapper ${errors.terms ? 'has-error' : ''}`}
            onClick={() => {
              setAgreedToTerms(!agreedToTerms);
              if (errors.terms) {
                setErrors({ ...errors, terms: '' });
              }
            }}
          >
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => {
                setAgreedToTerms(e.target.checked);
                if (errors.terms) {
                  setErrors({ ...errors, terms: '' });
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <label onClick={(e) => e.preventDefault()}>
              I have read and agree to the Terms of Use and Privacy Policy
            </label>
          </div>
          {errors.terms && <span className="terms-error">{errors.terms}</span>}
        </div>

        <button type="submit">Create account</button>
        <motion.div 
          className="auth-links"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Link to="/login">Already have an account? Login</Link>
        </motion.div>
      </motion.form>
      </motion.div>

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        isPrivacyPolicy={true}
      />

      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            className="terms-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '40px',
                maxWidth: '450px',
                width: '90%',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 24px',
                  background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '40px'
                }}
              >
                ✉️
              </motion.div>
              
              <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b', marginBottom: '16px' }}>
                Check Your Email!
              </h2>
              
              <p style={{ fontSize: '16px', color: '#475569', lineHeight: 1.6, marginBottom: '24px' }}>
                We've sent a verification link to <strong>{formData.email}</strong>
              </p>
              
              <div style={{
                padding: '16px',
                background: '#f0fdf4',
                borderRadius: '12px',
                border: '2px solid #bbf7d0',
                marginBottom: '24px'
              }}>
                <p style={{ fontSize: '14px', color: '#166534', margin: 0, lineHeight: 1.6 }}>
                  Please check your inbox (and spam folder) and click the verification link to activate your account.
                </p>
              </div>

              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/login');
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '999px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(21, 128, 61, 0.3)'
                }}
              >
                Go to Login
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Register;
